import React from 'react'
import {useSelector} from 'react-redux'

import {AppState} from '../../../../../Root.store'

const DISMISSED_ANNOUNCEMENTS_KEY = 'dismissedAnnouncements'

/**
 * Enum of available dismissible announcement types
 * Add new announcement types here to maintain type safety
 */
export enum AnnouncementType {
  /** Announcement shown when a mix has customization options available */
  MIX_CUSTOMIZATION = 'mixCustomization'
}

/**
 * Hook to manage dismissible announcements with localStorage persistence
 * Announcements are stored per-user to maintain separate dismiss states
 *
 * @param announcementType - Unique identifier for the announcement from AnnouncementType enum
 * @returns Object with isDismissed state and dismiss function
 */
export const useDismissibleAnnouncement = (announcementType: AnnouncementType) => {
  const userId = useSelector((state: AppState) => state.userProfile.userProfile?.user_id || '')

  /**
   * Get dismissed state from localStorage for current user and announcement type
   */
  const getDismissedState = (): boolean => {
    if (!userId) return false

    try {
      const stored = localStorage.getItem(`${DISMISSED_ANNOUNCEMENTS_KEY}-${userId}`)
      if (!stored) return false

      const dismissedAnnouncements = JSON.parse(stored)
      return dismissedAnnouncements[announcementType] ?? false
    } catch (error) {
      console.error('Error reading dismissed announcement state:', error)
      return false
    }
  }

  /**
   * Save dismissed state to localStorage for current user and announcement type
   */
  const saveDismissedState = () => {
    if (!userId) return

    try {
      const stored = localStorage.getItem(`${DISMISSED_ANNOUNCEMENTS_KEY}-${userId}`) || '{}'
      const dismissedAnnouncements = JSON.parse(stored)
      dismissedAnnouncements[announcementType] = true
      localStorage.setItem(`${DISMISSED_ANNOUNCEMENTS_KEY}-${userId}`, JSON.stringify(dismissedAnnouncements))
    } catch (error) {
      console.error('Error saving dismissed announcement state:', error)
    }
  }

  const [isDismissed, setIsDismissed] = React.useState(getDismissedState())

  /**
   * Dismiss the announcement - updates both localStorage and component state
   */
  const dismiss = React.useCallback(() => {
    saveDismissedState()
    setIsDismissed(true)
  }, [userId, announcementType])

  return {
    isDismissed,
    dismiss
  }
}
